import { Component, ElementRef, input, OnDestroy, output, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, switchMap } from 'rxjs';
import type { JsxgraphBoard, JsxgraphStatic } from './jsxgraph.model';
import { JsxgraphService } from './jsxgraph.service';

export type { JsxgraphBoard, JsxgraphStatic };

export interface JsxgraphBoardOptions {
  boundingbox?: [x1: number, y1: number, x2: number, y2: number];
  axis?: boolean;
  showNavigation?: boolean;
  showCopyright?: boolean;
  keepaspectratio?: boolean;
  [key: string]: unknown;
}

@Component({
  selector: 'dojo-jsxgraph',
  standalone: true,
  template: '<div #boardContainer class="jsxgraph-board"></div>',
  styles: [
    `
      :host {
        @apply block h-full w-full;
      }
      .jsxgraph-board {
        @apply h-full w-full;
      }
    `,
  ],
  host: { class: 'block h-full w-full' },
})
export class JsxgraphComponent implements OnDestroy {
  constructor(private readonly jsxgraphService: JsxgraphService) {
    combineLatest([toObservable(this.options), toObservable(this.boardContainer)])
      .pipe(
        filter(([options, container]) => !!options && !!container),
        switchMap(() => {
          this.destroyBoard();
          return this.initializeBoard();
        }),
      )
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  private readonly boardContainer = viewChild.required<ElementRef<HTMLDivElement>>('boardContainer');

  readonly boardId = input<string>(`jsxboard-${Math.random().toString(36).substring(2, 9)}`);

  readonly options = input<JsxgraphBoardOptions>({
    boundingbox: [-5, 5, 5, -5],
    axis: true,
    showNavigation: false,
    showCopyright: false,
  });

  readonly boardReady = output<JsxgraphBoard>();

  private board: JsxgraphBoard | null = null;

  ngOnDestroy(): void {
    this.destroyBoard();
  }

  private async initializeBoard(): Promise<void> {
    try {
      const instance = await this.jsxgraphService.loadJsxgraph();
      const container = this.boardContainer().nativeElement;

      container.id = this.boardId();

      if (instance) {
        this.board = instance.JSXGraph.initBoard(this.boardId(), this.options());
        this.boardReady.emit(this.board);
      }
    } catch (error) {
      console.error('Failed to initialize JSXGraph board:', error);
    }
  }

  private destroyBoard(): void {
    if (this.board && this.jsxgraphService.instance) {
      try {
        this.jsxgraphService.instance.JSXGraph.freeBoard(this.board);
      } catch (error) {
        console.error('Error freeing JSXGraph board:', error);
      }
      this.board = null;
    }
  }
}
